/**
 * FinanceHub.jsx — /finance
 * Canadian Finance Hub — free, no account needed
 * Email capture for newsletter/funnel
 *
 * Tools (26 total):
 *   FIRE Planning: FIRE Number, Coast FIRE, Fat FIRE, Barista FIRE, Windfall Impact, Purchase Impact
 *   Investing: Compound Interest, Loan Payment, Mortgage, Portfolio Rebalancing, Rental ROI,
 *              House Hacking, Cap Rate, Cash-on-Cash, Dividend Yield
 *   Savings: Savings Rate, Emergency Fund, HSA Growth, Inflation, Upwork Fee, EV Savings, SIP
 *   Canadian: RRSP, TFSA, Retirement, GST/VAT, Payroll, Currency Converter
 *   Business: Invoice Generator, Freelance Rate Calculator
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const T = {
  bg:'#07080c', bg2:'#0c0f18', bg3:'#111520', surf:'#161c2a',
  border:'rgba(255,255,255,.07)', borderH:'rgba(255,255,255,.13)',
  white:'#dce6fa', muted:'rgba(255,255,255,0.75)', dim:'rgba(255,255,255,0.75)',
  cyan:'#00c8f0', blue:'#4d8fff', green:'#2ecc71',
  teal:'#1abc9c', orange:'#e67e22', red:'#e74c3c',
  sans:'"Syne","DM Sans",system-ui,sans-serif',
  mono:'"JetBrains Mono",monospace',
};

const CATEGORIES = [
  {
    id:    'fire',
    icon:  '🔥',
    color:  T.orange,
    label: 'FIRE Planning',
    desc:  'Financial Independence, Retire Early',
    tools: [
      'FIRE Number Calculator',
      'Coast FIRE Calculator',
      'Fat FIRE Calculator',
      'Barista FIRE Calculator',
      'Windfall Impact on FIRE Date',
      'Purchase Impact on FIRE Date',
    ],
  },
  {
    id:    'canadian',
    icon:  '🍁',
    color:  T.teal,
    label: 'Canadian Specific',
    desc:  'RRSP, TFSA, GST/HST, payroll — built for Canada',
    tools: [
      'RRSP Contribution Calculator (2026)',
      'TFSA Growth Calculator',
      'Retirement Calculator',
      'GST/HST Calculator',
      'Payroll Calculator (ON, BC, AB, QC)',
      'Currency Converter (10 currencies)',
    ],
  },
  {
    id:    'investing',
    icon:  '📈',
    color:  T.green,
    label: 'Investing & Real Estate',
    tools: [
      'Compound Interest Calculator',
      'Mortgage Calculator',
      'Loan Payment Calculator',
      'Portfolio Rebalancing',
      'Rental Property ROI',
      'House Hacking Calculator',
      'Cap Rate Calculator',
      'Cash-on-Cash Return',
      'Dividend Yield Calculator',
    ],
  },
  {
    id:    'savings',
    icon:  '💰',
    color:  T.cyan,
    label: 'Savings & Budgeting',
    tools: [
      'Savings Rate Calculator',
      'Emergency Fund Calculator',
      'Inflation & Salary Calculator',
      'HSA Growth Calculator',
      'EV vs Gas Savings',
      'SIP Calculator',
    ],
  },
  {
    id:    'business',
    icon:  '🧾',
    color:  '#b78cff',
    label: 'Business & Freelance',
    desc:  'Also available in the Freelancer portal',
    tools: [
      'Invoice Generator (no watermark)',
      'Freelance Rate Calculator',
      'Profit Margin Calculator',
      'Markup Calculator',
    ],
  },
];

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Email capture component ─────────────────────────────────────────
function EmailCapture() {
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/finance/subscribe/`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email }),
      });
      if (res.ok) setDone(true);
      else throw new Error('Could not subscribe. Please try again.');
    } catch(err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding:'32px', borderRadius:14,
      background:`linear-gradient(135deg, rgba(26,188,156,.07), rgba(0,200,240,.07))`,
      border:`1px solid rgba(26,188,156,.2)`,
      textAlign:'center', margin:'44px 0 0',
    }}>
      {done ? (
        <>
          <div style={{ fontSize:32, marginBottom:12 }}>✅</div>
          <div style={{ fontSize:16, fontWeight:700, color:T.white, marginBottom:6 }}>You're in!</div>
          <div style={{ fontSize:13, color:T.muted }}>
            We'll send you Canadian finance tips, tool updates, and calculator guides. No spam.
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize:28, marginBottom:12 }}>🍁</div>
          <h3 style={{ fontSize:18, fontWeight:800, color:T.white, marginBottom:6, letterSpacing:'-.01em' }}>
            Get the Canadian FIRE Planning Guide
          </h3>
          <p style={{ fontSize:13.5, color:T.muted, lineHeight:1.7, marginBottom:20, maxWidth:400, margin:'0 auto 20px' }}>
            Free guide: how to use RRSP + TFSA to retire early in Canada. Plus calculator tips and updates.
          </p>
          <form onSubmit={handleSubmit} style={{ display:'flex', gap:9, maxWidth:380, margin:'0 auto' }}>
            <input
              type="email" value={email} onChange={e=>setEmail(e.target.value)}
              placeholder="you@example.com" required
              style={{
                flex:1, padding:'10px 14px',
                background:T.bg, border:`1px solid ${T.border}`,
                borderRadius:8, color:T.white, fontSize:13.5,
                fontFamily:T.sans, outline:'none',
              }}
            />
            <button type="submit" disabled={loading} style={{
              padding:'10px 18px', borderRadius:8, border:'none',
              background:`linear-gradient(135deg,${T.teal},${T.cyan})`,
              color:'#07080c', fontSize:13.5, fontWeight:700,
              fontFamily:T.sans, cursor:'pointer', whiteSpace:'nowrap',
            }}>
              {loading ? '…' : 'Get guide →'}
            </button>
          </form>
          {error && <div style={{ fontSize:12, color:T.red, marginTop:8 }}>{error}</div>}
          <div style={{ fontSize:11, color:T.dim, marginTop:10 }}>
            No spam. Unsubscribe any time.
          </div>
        </>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────
export default function FinanceHub() {
  const [hovered, setHovered] = useState(null);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        input::placeholder { color:rgba(255,255,255,0.75)!important; }
        input:focus { border-color:rgba(0,200,240,.4)!important; outline:none; }
      `}</style>

      <div style={{ background:T.bg, minHeight:'100vh', fontFamily:T.sans }}>

        {/* Nav */}
        <nav style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'13px 28px', background:T.bg2,
          borderBottom:`1px solid ${T.border}`, position:'sticky', top:0, zIndex:10,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <Link to="/" style={{ fontSize:16, fontWeight:900, color:T.white, textDecoration:'none', letterSpacing:'-.02em' }}>
              Tylent<span style={{ color:T.cyan }}>ed</span>
            </Link>
            <span style={{ color:T.dim }}>›</span>
            <span style={{ fontSize:14, fontWeight:700, color:T.white }}>🍁 Canadian Finance Hub</span>
            <span style={{ padding:'2px 8px', borderRadius:20, background:`${T.teal}12`, border:`1px solid ${T.teal}35`, fontSize:10, fontWeight:700, color:T.teal }}>
              Free · No sign-up
            </span>
          </div>
          <div style={{ display:'flex', gap:16 }}>
            {[
              ['/job-search','🎯 Job Search'],
              ['/freelancer','💼 Freelancer'],
              ['/developer','⌨️ Developer'],
              ['/tools','🛠️ Tools'],
            ].map(([path, label]) => (
              <Link key={path} to={path} style={{ fontSize:12.5, color:T.muted, textDecoration:'none' }}>
                {label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Hero */}
        <div style={{
          padding:'48px 28px 36px', textAlign:'center',
          background:`linear-gradient(180deg, rgba(26,188,156,.05) 0%, transparent 100%)`,
          borderBottom:`1px solid ${T.border}`,
        }}>
          <div style={{
            display:'inline-flex', alignItems:'center', gap:7,
            padding:'4px 13px', borderRadius:20,
            background:'rgba(26,188,156,.08)', border:'1px solid rgba(26,188,156,.2)',
            fontSize:11.5, fontWeight:700, color:T.teal,
            letterSpacing:'.08em', textTransform:'uppercase', marginBottom:18,
          }}>
            🍁 Built for Canadians
          </div>
          <h1 style={{ fontSize:'clamp(1.6rem,3.5vw,2.5rem)', fontWeight:900, color:T.white, letterSpacing:'-.03em', marginBottom:10 }}>
            Canadian Finance Hub
          </h1>
          <p style={{ fontSize:14.5, color:T.muted, maxWidth:540, margin:'0 auto 24px', lineHeight:1.75 }}>
            26 free calculators covering RRSP, TFSA, FIRE planning, mortgage, payroll, GST/HST, and more — all built for the Canadian market.
          </p>
          <div style={{ display:'flex', justifyContent:'center', gap:16, flexWrap:'wrap', fontSize:13, color:T.muted }}>
            {['26 calculators', 'CAD + 10 currencies', '2026 RRSP limits', 'All provinces'].map(tag => (
              <span key={tag} style={{ display:'flex', alignItems:'center', gap:5 }}>
                <span style={{ color:T.teal }}>✓</span> {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Tool categories */}
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'44px 28px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))', gap:18 }}>
            {CATEGORIES.map(cat => (
              <div
                key={cat.id}
                onMouseEnter={() => setHovered(cat.id)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  padding:'22px 20px', borderRadius:12,
                  border:`1px solid ${hovered===cat.id ? cat.color+'50' : T.border}`,
                  background: hovered===cat.id ? `${cat.color}06` : 'rgba(255,255,255,.02)',
                  transition:'all .2s',
                }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:8 }}>
                  <span style={{ fontSize:20 }}>{cat.icon}</span>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:T.white }}>{cat.label}</div>
                    {cat.desc && <div style={{ fontSize:11.5, color:T.dim, marginTop:1 }}>{cat.desc}</div>}
                  </div>
                </div>
                <ul style={{ listStyle:'none', display:'flex', flexDirection:'column', gap:5 }}>
                  {cat.tools.map(tool => (
                    <li key={tool} style={{
                      display:'flex', alignItems:'center', gap:7,
                      fontSize:12.5, color:T.muted, padding:'3px 0',
                      borderBottom:`1px solid ${T.border}`,
                    }}>
                      <span style={{ color:cat.color, fontSize:11 }}>→</span>
                      {tool}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Email capture */}
          <EmailCapture/>

          {/* AI tools callout */}
          <div style={{
            marginTop:28, padding:'20px 24px', borderRadius:10,
            background:`rgba(255,255,255,.025)`, border:`1px solid ${T.border}`,
            display:'flex', alignItems:'center', justifyContent:'space-between',
            flexWrap:'wrap', gap:12,
          }}>
            <div>
              <div style={{ fontSize:13.5, fontWeight:600, color:T.white, marginBottom:3 }}>
                💼 Running a freelance business?
              </div>
              <div style={{ fontSize:12.5, color:T.muted }}>
                The Freelancer portal has AI-powered proposal writing, outreach emails, and more.
              </div>
            </div>
            <Link to="/freelancer" style={{
              padding:'8px 16px', borderRadius:7,
              border:`1px solid rgba(183,140,255,.4)`, background:'rgba(183,140,255,.08)',
              color:'#b78cff', fontSize:13, fontWeight:600, textDecoration:'none',
            }}>
              Freelancer Tools →
            </Link>
          </div>
        </div>

        {/* Footer */}
        <footer style={{ borderTop:`1px solid ${T.border}`, padding:'24px 28px', background:T.bg2, textAlign:'center' }}>
          <div style={{ fontSize:12, color:T.dim, lineHeight:1.7 }}>
            All calculators are for estimation purposes only and do not constitute financial advice.
            RRSP limits sourced from CRA for the 2026 tax year.
          </div>
        </footer>
      </div>
    </>
  );
}
